import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, TestRunWithProgress } from '../services/api';
import { TestExecutionWithDetails } from '../types';
import TestExecutionModal from '../components/TestExecutionModal';
import { priorityBadge } from '../components/ViewDetailModal';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';
import { emailToUsername, parseJiraTickets } from '../utils/formatters';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { ProductTour } from '../components/ProductTour';
import { useProductTour, TOUR_IDS } from '../hooks/useProductTour';
import { testRunExecuteTourSteps } from '../tours/tourSteps';
import { useRBAC } from '../components/ProtectedRoute';
import './TestRunDetailPage.css';

const PAGE_SIZE = 10;

const TestRunDetailPage: React.FC = () => {
  const { testRunId } = useParams<{ testRunId: string }>();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirmDialog();
  const { user: currentUser } = useAuth();
  const { canWrite } = useRBAC();
  const canReopenRun = currentUser?.role === 'Admin' || currentUser?.role === 'QA_Engineer';
  const [testRun, setTestRun] = useState<TestRunWithProgress | null>(null);
  const [executions, setExecutions] = useState<TestExecutionWithDetails[]>([]);

  // The Execute button and bulk-evidence checkbox column only render for an active
  // (non-immutable) run that has at least one execution row — those tour steps' targets
  // wouldn't exist otherwise, so auto-start waits for this and the manual trigger warns instead.
  const tourReady = !!testRun && testRun.status !== 'COMPLETED' && testRun.status !== 'ABORTED' && executions.length > 0;
  const { run: tourRun, start: startTour, handleCallback: tourCallback } = useProductTour(TOUR_IDS.TEST_RUN_EXECUTE, true, tourReady);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingEnvironment, setEditingEnvironment] = useState(false);
  const [environmentDraft, setEnvironmentDraft] = useState('');
  const [savingEnvironment, setSavingEnvironment] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState<TestExecutionWithDetails | null>(null);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Evidence download (for immutable runs)
  const [evidenceDropdown, setEvidenceDropdown] = useState<string | null>(null); // executionId with open dropdown
  const [evidenceMap, setEvidenceMap] = useState<Record<string, any[]>>({}); // executionId → evidence list
  const [evidenceLoading, setEvidenceLoading] = useState<Record<string, boolean>>({}); // executionId → loading
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [selectedEvidenceExecution, setSelectedEvidenceExecution] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({}); // evidence.id → blob object URL
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});
  const [zoomedFileId, setZoomedFileId] = useState<string | null>(null); // which evidence file (if any) is shown at full size

  // Column configuration — Scenario Title/Status/Defect/Evidence always shown; Executed By/At optional
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'title', label: 'Scenario Title', visible: true, required: true },
    { key: 'status', label: 'Status', visible: true, required: true },
    { key: 'executed_by', label: 'Executed By', visible: false },
    { key: 'executed_at', label: 'Executed At', visible: false },
    { key: 'defect', label: 'Defect / Note', visible: true, required: true },
    { key: 'evidence', label: 'Evidence', visible: true, required: true },
  ]);

  // Load column preferences from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('test-run-executions-columns');
    if (saved) {
      try {
        const visibilityMap = JSON.parse(saved);
        setColumns(cols => cols.map(col => ({
          ...col,
          visible: visibilityMap[col.key] ?? col.visible
        })));
      } catch (e) {
        console.error('Failed to load column preferences:', e);
      }
    }
  }, []);

  // Bulk evidence upload
  const [selectedExecutionIds, setSelectedExecutionIds] = useState<Set<string>>(new Set());
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  // Bulk status change
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<'PASS' | 'FAIL' | 'BLOCKED'>('PASS');
  const [bulkStatusComment, setBulkStatusComment] = useState('');
  const [bulkStatusDefect, setBulkStatusDefect] = useState('');
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false);
  const [bulkStatusProgress, setBulkStatusProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkStatusError, setBulkStatusError] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (testRunId) {
      loadTestRun();
      loadExecutions();
    }
  }, [testRunId, statusFilter]);

  // Filter + search + paginate (memoized so the eager evidence-fetch effect below
  // can depend on a stable `paginated` reference instead of re-deriving it inline)
  const filtered = React.useMemo(() => {
    const stripHtmlLocal = (html: string) => html.replace(/<[^>]*>/g, '');
    return executions
      .filter(e => {
        const matchSearch = !searchTerm ||
          stripHtmlLocal(e.test_case_description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.test_case_display_id?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchSearch;
      })
      .sort((a, b) => {
        // Sort by numeric part of ID (TS-00153 → 153, TC-00153 → 153)
        const na = parseInt((a.test_case_display_id || '').replace(/\D/g, '')) || 0;
        const nb = parseInt((b.test_case_display_id || '').replace(/\D/g, '')) || 0;
        return na - nb;
      });
  }, [executions, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = React.useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  // Columns as shown in the selector/header — the "title" column's label follows the run type
  const titleColumnLabel = testRun?.run_type === 'TEST_CASE' ? 'Test Case Title' : 'Scenario Title';
  const displayColumns = React.useMemo(
    () => columns.map(col => col.key === 'title' ? { ...col, label: titleColumnLabel } : col),
    [columns, titleColumnLabel]
  );
  const isColVisible = (key: string) => columns.find(c => c.key === key)?.visible ?? false;

  // Preview modal Prev/Next — browses the full filtered list (not just the current
  // page), so navigating never stops just because the current page ran out of rows.
  const previewIndex = filtered.findIndex(e => e.id === selectedEvidenceExecution);
  const previewExecution = previewIndex >= 0 ? filtered[previewIndex] : null;
  const goToPreviewIndex = (index: number) => {
    if (index < 0 || index >= filtered.length) return;
    setSelectedEvidenceExecution(filtered[index].id);
    setZoomedFileId(null);
    ensureEvidenceFetched(filtered[index].id);
  };
  const openPreview = (executionId: string) => {
    setSelectedEvidenceExecution(executionId);
    setZoomedFileId(null);
    setShowEvidenceModal(true);
  };

  // Shared by the eager page-load fetch below AND on-demand fetches when the preview
  // modal's Prev/Next navigation lands on an execution outside the current page.
  const fetchedEvidenceIdsRef = useRef<Set<string>>(new Set());
  const ensureEvidenceFetched = (id: string) => {
    if (fetchedEvidenceIdsRef.current.has(id)) return;
    fetchedEvidenceIdsRef.current.add(id);
    (async () => {
      try {
        const files = await api.getExecutionEvidence(id);
        setEvidenceMap(prev => ({ ...prev, [id]: files }));
      } catch {
        fetchedEvidenceIdsRef.current.delete(id); // allow retry on a later pass
      }
    })();
  };

  // Eagerly fetch evidence for every execution visible on the current page, for ANY
  // run status — this is what lets the Preview action show up regardless of whether
  // the run is NOT_STARTED/IN_PROGRESS/COMPLETED, as soon as evidence exists.
  useEffect(() => {
    paginated.forEach(e => ensureEvidenceFetched(e.id));
  }, [paginated]);

  // Call after uploading/changing evidence for an execution so the eager-fetch effect
  // above re-checks it instead of trusting a stale "already checked, no evidence" result.
  const invalidateEvidenceCache = (id: string) => {
    fetchedEvidenceIdsRef.current.delete(id);
    setEvidenceMap(prev => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const loadTestRun = async () => {
    try {
      const run = await api.getTestRun(testRunId!);
      setTestRun(run);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test run');
    }
  };

  const loadExecutions = async () => {
    try {
      setLoading(true);
      const execs = await api.getTestRunExecutions(testRunId!, statusFilter || undefined);
      setExecutions(execs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRun = async () => {
    try {
      await api.startTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to start test run');
    }
  };

  const handleCloseRun = async () => {
    const confirmed = await confirm(
      'Close Test Run',
      'Are you sure you want to close this test run? It will become immutable and no further changes can be made.',
      'Close Test Run',
      'lock',
      false   // not danger — this is a finalize, not a delete
    );
    if (!confirmed) return;
    try {
      await api.closeTestRun(testRunId!);
      await loadTestRun();
    } catch (err: any) {
      setError(err.message || 'Failed to close test run');
    }
  };

  const handleDeleteRun = async () => {
    const confirmed = await confirm(
      'Delete Test Run',
      `Are you sure you want to permanently delete "${testRun?.name}"? This cannot be undone. Only Not Started runs can be deleted.`,
      'Delete Test Run',
      'delete',
      true   // danger — this permanently removes the run and its executions
    );
    if (!confirmed) return;
    try {
      setDeleting(true);
      await api.deleteTestRun(testRunId!);
      navigate('/test-runs');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to delete test run');
      setDeleting(false);
    }
  };

  const handleReopenRun = async () => {
    const confirmed = await confirm(
      'Reopen Test Run',
      'This will reopen the test run and set it back to IN PROGRESS. You can then upload evidence and re-execute test cases. Only do this if the run was closed by mistake.',
      'Reopen Run',
      'warning',
      false
    );
    if (!confirmed) return;
    try {
      setReopening(true);
      await api.reopenTestRun(testRunId!);
      setSuccessMessage('Test run has been reopened and is now IN PROGRESS.');
      await loadTestRun();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to reopen test run');
    } finally {
      setReopening(false);
    }
  };

  const handleStartEditEnvironment = () => {
    setEnvironmentDraft(testRun?.environment || '');
    setEditingEnvironment(true);
  };

  const handleCancelEditEnvironment = () => {
    setEditingEnvironment(false);
  };

  const handleSaveEnvironment = async () => {
    if (!environmentDraft || environmentDraft === testRun?.environment) {
      setEditingEnvironment(false);
      return;
    }
    try {
      setSavingEnvironment(true);
      await api.updateTestRunEnvironment(testRunId!, environmentDraft);
      setSuccessMessage(`Environment updated to ${environmentDraft}.`);
      setEditingEnvironment(false);
      await loadTestRun();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to update environment');
    } finally {
      setSavingEnvironment(false);
    }
  };

  const handleSyncNew = async () => {    try {
      setSyncing(true);
      setSuccessMessage(null);
      const result = await api.syncNewToTestRun(testRunId!);
      if (result.added === 0) {
        setSuccessMessage('Already up to date — no new scenarios to sync.');
      } else {
        setSuccessMessage(`${result.added} new scenario${result.added !== 1 ? 's' : ''} added to the test run.`);
        await loadTestRun();
        await loadExecutions();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync new scenarios');
    } finally {
      setSyncing(false);
    }
  };

  // Selection helpers
  const toggleSelectExecution = (id: string) => {
    setSelectedExecutionIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedExecutionIds.size === paginated.length) {
      setSelectedExecutionIds(new Set());
    } else {
      setSelectedExecutionIds(new Set(paginated.map(e => e.id)));
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || selectedExecutionIds.size === 0) return;
    setBulkUploading(true);
    setBulkError(null);
    const ids = Array.from(selectedExecutionIds);
    setBulkProgress({ done: 0, total: ids.length });
    let failed = 0;

    // Resolve the display ID of the first execution (for the reference note)
    const firstExec = executions.find(e => e.id === ids[0]);
    const firstDisplayId = firstExec?.test_case_display_id || ids[0].slice(0, 8);

    for (let i = 0; i < ids.length; i++) {
      try {
        if (i === 0) {
          // First execution — mark PASS + upload the file
          await api.updateExecution(ids[i], { execution_status: 'PASS' });
          await api.uploadExecutionEvidence(ids[i], bulkFile, 0);
          invalidateEvidenceCache(ids[i]); // this is the one that actually got a real file
        } else {
          // Remaining executions — mark PASS + set a reference note, NO file upload
          await api.updateExecution(ids[i], {
            execution_status: 'PASS',
            defect_reference: `Evidence same as ${firstDisplayId}`,
          });
        }
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkUploading(false);
    if (failed > 0) {
      setBulkError(`${failed} upload(s) failed. ${ids.length - failed} succeeded.`);
    } else {
      setShowBulkUpload(false);
      setBulkFile(null);
      setBulkProgress(null);
      setSelectedExecutionIds(new Set());
      await loadTestRun();
      await loadExecutions();
    }
  };

  const handleBulkStatusChange = async () => {
    if (selectedExecutionIds.size === 0) return;
    setBulkStatusUpdating(true);
    setBulkStatusError(null);
    const ids = Array.from(selectedExecutionIds);
    setBulkStatusProgress({ done: 0, total: ids.length });
    let failed = 0;

    for (let i = 0; i < ids.length; i++) {
      try {
        await api.updateExecution(ids[i], {
          execution_status: bulkStatus,
          comment: bulkStatusComment.trim() || undefined,
          defect_reference: bulkStatusDefect.trim() || undefined,
        });
      } catch {
        failed++;
      }
      setBulkStatusProgress({ done: i + 1, total: ids.length });
    }
    setBulkStatusUpdating(false);
    if (failed > 0) {
      setBulkStatusError(`${failed} update(s) failed. ${ids.length - failed} succeeded.`);
    } else {
      setShowBulkStatus(false);
      setBulkStatusComment('');
      setBulkStatusDefect('');
      setBulkStatusProgress(null);
      setSelectedExecutionIds(new Set());
      await loadTestRun();
      await loadExecutions();
    }
  };

  const handleExecuteTestCase = (execution: TestExecutionWithDetails) => {
    setSelectedExecution(execution);
    setShowExecutionModal(true);
  };

  const handleExecutionComplete = () => {
    if (selectedExecution) invalidateEvidenceCache(selectedExecution.id);
    setShowExecutionModal(false);
    setSelectedExecution(null);
    loadTestRun();
    loadExecutions();
  };

  // Close evidence dropdown when clicking outside (no longer needed with modal)
  // Keeping for reference but not used anymore

  const handleEvidenceDownloadClick = async (executionId: string) => {
    // If already loaded, show modal
    if (evidenceMap[executionId] !== undefined) {
      const files = evidenceMap[executionId];
      if (files.length === 0) return; // nothing to do
      if (files.length === 1) {
        triggerEvidenceDownload(files[0]);
        return;
      }
      setSelectedEvidenceExecution(executionId);
      setShowEvidenceModal(true);
      return;
    }

    // Fetch evidence list
    setEvidenceLoading(prev => ({ ...prev, [executionId]: true }));
    try {
      const files = await api.getExecutionEvidence(executionId);
      setEvidenceMap(prev => ({ ...prev, [executionId]: files }));
      if (files.length === 0) {
        // nothing — button will become disabled on next render
      } else if (files.length === 1) {
        triggerEvidenceDownload(files[0]);
      } else {
        setSelectedEvidenceExecution(executionId);
        setShowEvidenceModal(true);
      }
    } catch {
      // silently ignore — button stays enabled for retry
    } finally {
      setEvidenceLoading(prev => ({ ...prev, [executionId]: false }));
    }
  };

  const triggerEvidenceDownload = async (evidence: any) => {
    try {
      // Step 1: get the URL info via the authenticated API
      const urlInfo = await api.getEvidenceUrl(evidence.id);

      if (urlInfo.storage === 's3' && urlInfo.url) {
        // S3: presigned URL — open directly, no auth header needed
        const a = document.createElement('a');
        a.href = urlInfo.url;
        a.download = urlInfo.file_name || evidence.file_name || evidence.id;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // Local: fetch as blob through the authenticated axios client
        const axiosInstance = (api as any).client;
        const response = await axiosInstance.get(
          `/api/v1/test-runs/evidence/${evidence.id}/download`,
          { responseType: 'blob' }
        );
        const blob: Blob = response.data;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = urlInfo.file_name || evidence.file_name || evidence.id;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
      }
    } catch (err: any) {
      setError(`Failed to download evidence: ${err?.message || 'Unknown error'}`);
    }
  };

  const getEvidenceKind = (fileName: string): 'image' | 'video' | 'other' => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    if (ext && imageExts.includes(ext)) return 'image';
    if (ext && videoExts.includes(ext)) return 'video';
    return 'other';
  };

  // Fetch inline preview URLs (image/video only) for whichever execution's evidence is
  // currently open in the preview modal. Storage-aware, mirroring triggerEvidenceDownload:
  // for S3-stored evidence, /download 302-redirects to a presigned S3 URL — a raw fetch()
  // follows that redirect cross-origin and gets blocked by CORS when reading the body, so
  // S3 files must use the presigned URL directly as the <img>/<video> src (no fetch needed;
  // browsers don't require CORS just to render an image/video, only to read its bytes in JS).
  // Local-storage evidence has no such redirect, so it's fetched as an authenticated blob.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showEvidenceModal || !selectedEvidenceExecution) return;
    const files = evidenceMap[selectedEvidenceExecution] || [];
    files.forEach(async (file: any) => {
      if (previewUrls[file.id] || previewLoading[file.id]) return;
      if (getEvidenceKind(file.file_name || '') === 'other') return;
      setPreviewLoading(prev => ({ ...prev, [file.id]: true }));
      try {
        const urlInfo = await api.getEvidenceUrl(file.id);
        if (urlInfo.storage === 's3' && urlInfo.url) {
          setPreviewUrls(prev => ({ ...prev, [file.id]: urlInfo.url as string }));
        } else {
          const authTokens = localStorage.getItem('auth_tokens');
          const token = authTokens ? JSON.parse(authTokens).access_token : '';
          const response = await fetch(api.getEvidenceDownloadUrl(file.id), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPreviewUrls(prev => ({ ...prev, [file.id]: objectUrl }));
          }
        }
      } catch {
        // leave unpreviewable — the download button in the modal still works
      } finally {
        setPreviewLoading(prev => ({ ...prev, [file.id]: false }));
      }
    });
  }, [showEvidenceModal, selectedEvidenceExecution, evidenceMap]);

  // Revoke all preview object URLs when the page unmounts
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRunStatusClass = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'run-status-inprogress';
      case 'COMPLETED':   return 'run-status-completed';
      case 'ABORTED':     return 'run-status-aborted';
      default:            return 'run-status-notstarted';
    }
  };

  const getRunStatusLabel = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return 'IN PROGRESS';
      case 'NOT_STARTED': return 'NOT STARTED';
      default: return status;
    }
  };

  const getExecStatusClass = (status: string) => {
    switch (status) {
      case 'PASS':    return 'exec-pass';
      case 'FAIL':    return 'exec-fail';
      case 'BLOCKED': return 'exec-blocked';
      default:        return 'exec-notrun';
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLine = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
      + ' · ' + new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && !testRun) {
    return <div className="trd-page"><div className="trd-loading">Loading...</div></div>;
  }
  if (!testRun) {
    return <div className="trd-page"><div className="trd-error">Test run not found</div></div>;
  }

  const isImmutable = testRun.status === 'COMPLETED' || testRun.status === 'ABORTED';

  // Progress bar segments
  const total = testRun.total_cases || 1;
  const passW    = Math.round((testRun.pass_count / total) * 100);
  const failW    = Math.round((testRun.fail_count / total) * 100);
  const blockedW = Math.round((testRun.blocked_count / total) * 100);
  const notRunW  = Math.max(0, 100 - passW - failW - blockedW);


  const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'PASS', label: 'Pass' },
    { value: 'FAIL', label: 'Fail' },
    { value: 'BLOCKED', label: 'Blocked' },
    { value: 'NOT_RUN', label: 'Not Run' },
  ];

  const currentStatusLabel = STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'All Statuses';

  return (
    <div className="trd-page">
      <ProductTour steps={testRunExecuteTourSteps} run={tourRun} onCallback={tourCallback} />
      {dialog}
      {/* Back link */}
      <button className="trd-back" onClick={() => navigate('/test-runs')}>
        ← Back to Test Runs
      </button>

      {error && <div className="trd-error-banner">{error}<button onClick={() => setError(null)}>×</button></div>}
      {successMessage && <div className="trd-success-banner">{successMessage}<button onClick={() => setSuccessMessage(null)}>×</button></div>}

      {/* Title row */}
      <div className="trd-title-row">
        <div className="trd-title-left">
          <h1 className="trd-title">{testRun.name}</h1>
          <span className={`trd-run-status ${getRunStatusClass(testRun.status)}`}>
            {getRunStatusLabel(testRun.status)}
          </span>
        </div>
        <div className="trd-title-actions">
          {canWrite && testRun.status === 'NOT_STARTED' && (
            <button className="trd-btn-start" onClick={handleStartRun}>▶ Start Execution</button>
          )}
          {canWrite && (testRun.status === 'NOT_STARTED' || testRun.status === 'IN_PROGRESS') && (
            <button
              className="trd-btn-sync"
              onClick={handleSyncNew}
              disabled={syncing}
              title="Add scenarios/test cases created after this run was created"
            >
              {syncing ? '⟳ Syncing...' : '⟳ Sync New'}
            </button>
          )}
          {canWrite && testRun.status === 'IN_PROGRESS' && (
            <button className="trd-btn-close" onClick={handleCloseRun}>Close Test Run</button>
          )}
          {(testRun.status === 'COMPLETED' || testRun.status === 'ABORTED') && canReopenRun && (
            <button
              className="trd-btn-reopen"
              onClick={handleReopenRun}
              disabled={reopening}
              title="Admin/QA Engineer only: reopen this run to allow further execution and evidence upload"
            >
              {reopening ? '↩ Reopening...' : '↩ Reopen Run'}
            </button>
          )}
          {canWrite && testRun.status === 'NOT_STARTED' && (
            <button
              className="trd-btn-delete"
              onClick={handleDeleteRun}
              disabled={deleting}
              title="Delete this test run — only possible before execution starts"
            >
              {deleting ? 'Deleting...' : '🗑 Delete Test Run'}
            </button>
          )}
        </div>
      </div>

      {/* Info + Progress row */}
      <div className="trd-top-row">
        {/* Execution context */}
        <div className="trd-context-card">
          <div className="trd-context-label">EXECUTION CONTEXT</div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Build Version</span>
            <span className="trd-ctx-val trd-ctx-mono">{testRun.build_version || '—'}</span>
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Environment</span>
            {editingEnvironment ? (
              <div className="trd-env-edit">
                <select
                  className="trd-env-edit-select"
                  value={environmentDraft}
                  onChange={(e) => setEnvironmentDraft(e.target.value)}
                  disabled={savingEnvironment}
                  autoFocus
                >
                  <option value="">Select environment...</option>
                  <option value="Stage">Stage</option>
                  <option value="Preprod">Preprod</option>
                  <option value="PROD">Production</option>
                </select>
                <button
                  className="trd-env-edit-btn trd-env-edit-save"
                  onClick={handleSaveEnvironment}
                  disabled={savingEnvironment || !environmentDraft}
                  title="Save"
                >
                  {savingEnvironment ? '…' : '✓'}
                </button>
                <button
                  className="trd-env-edit-btn trd-env-edit-cancel"
                  onClick={handleCancelEditEnvironment}
                  disabled={savingEnvironment}
                  title="Cancel"
                >
                  ×
                </button>
              </div>
            ) : (
              <span className="trd-ctx-val trd-ctx-bold trd-env-display">
                {testRun.environment || '—'}
                {canWrite && (
                  <button
                    className="trd-env-edit-trigger"
                    onClick={handleStartEditEnvironment}
                    title="Correct the environment (e.g. fix a mistakenly duplicated run)"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="trd-context-row">
            <span className="trd-ctx-key">Created By</span>
            <div className="trd-ctx-val">
              <span className="trd-ctx-bold">{emailToUsername(testRun.created_by)}</span>
              <span className="trd-ctx-sub">{formatDateLine(testRun.created_at)}</span>
            </div>
          </div>
          {testRun.started_at && (
            <div className="trd-context-row">
              <span className="trd-ctx-key">Started By</span>
              <div className="trd-ctx-val">
                <span className="trd-ctx-bold">{testRun.started_by ? emailToUsername(testRun.started_by) : 'System'}</span>
                <span className="trd-ctx-sub">{formatDateLine(testRun.started_at)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Global progress */}
        <div className="trd-progress-card">
          <div className="trd-progress-top">
            <span className="trd-progress-label">GLOBAL PROGRESS</span>
            <span className="trd-progress-pct">
              <strong>{Math.round(testRun.progress_percentage)}%</strong>
              <span className="trd-progress-complete"> Complete</span>
            </span>
          </div>

          {/* Segmented progress bar */}
          <div className="trd-progress-bar">
            {passW > 0    && <div className="trd-seg trd-seg-pass"    style={{ width: `${passW}%` }} />}
            {failW > 0    && <div className="trd-seg trd-seg-fail"    style={{ width: `${failW}%` }} />}
            {blockedW > 0 && <div className="trd-seg trd-seg-blocked" style={{ width: `${blockedW}%` }} />}
            {notRunW > 0  && <div className="trd-seg trd-seg-notrun"  style={{ width: `${notRunW}%` }} />}
          </div>

          {/* Metric pills */}
          <div className="trd-metrics">
            <div className="trd-metric">
              <span className="trd-metric-label">TOTAL</span>
              <span className="trd-metric-val">{testRun.total_cases}</span>
            </div>
            <div className="trd-metric trd-metric-pass">
              <span className="trd-metric-label">PASSED</span>
              <span className="trd-metric-val">{testRun.pass_count}</span>
            </div>
            <div className="trd-metric trd-metric-fail">
              <span className="trd-metric-label">FAILED</span>
              <span className="trd-metric-val">{testRun.fail_count}</span>
            </div>
            <div className="trd-metric trd-metric-blocked">
              <span className="trd-metric-label">BLOCKED</span>
              <span className="trd-metric-val">{testRun.blocked_count}</span>
            </div>
            <div className="trd-metric trd-metric-notrun">
              <span className="trd-metric-label">NOT RUN</span>
              <span className="trd-metric-val">{testRun.not_run_count}</span>
            </div>
            <div className="trd-metric trd-metric-rate">
              <span className="trd-metric-label">PASS RATE</span>
              <span className="trd-metric-val">{testRun.pass_percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Executions table */}
      <div className="trd-table-card">
        <div className="trd-table-header">
          <h2 className="trd-table-title">Test Execution Details</h2>
          <div className="trd-table-controls">
            {/* Status filter dropdown */}
            <div
              className="trd-status-filter"
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowStatusDropdown(false); }}
            >
              <button
                className="trd-filter-btn"
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {currentStatusLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="trd-filter-dropdown">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`trd-filter-option${statusFilter === opt.value ? ' selected' : ''}`}
                      onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); setPage(1); }}
                    >
                      {statusFilter === opt.value && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="trd-search-wrap">
              <svg className="trd-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="trd-search"
                type="text"
                placeholder="Search test cases..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              />
            </div>

            <ColumnSelector
              columns={displayColumns}
              onColumnsChange={setColumns}
              storageKey="test-run-executions-columns"
            />
          </div>
        </div>

        {loading ? (
          <div className="trd-loading">Loading test cases...</div>
        ) : (
          <>
            {/* Bulk action bar — shown when rows are selected */}
            {canWrite && !isImmutable && selectedExecutionIds.size > 0 && (
              <div className="trd-bulk-bar">
                <span className="trd-bulk-count">{selectedExecutionIds.size} selected</span>
                <button
                  className="trd-bulk-btn"
                  onClick={() => { setBulkFile(null); setBulkError(null); setBulkProgress(null); setShowBulkUpload(true); }}
                >
                  📎 Upload Evidence to Selected ({selectedExecutionIds.size})
                </button>
                <button
                  className="trd-bulk-btn trd-bulk-btn-status"
                  onClick={() => {
                    setBulkStatus('PASS');
                    setBulkStatusComment('');
                    setBulkStatusDefect('');
                    setBulkStatusError(null);
                    setBulkStatusProgress(null);
                    setShowBulkStatus(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                  Change Status ({selectedExecutionIds.size})
                </button>
                <button className="trd-bulk-clear" onClick={() => setSelectedExecutionIds(new Set())}>
                  Clear selection
                </button>
              </div>
            )}
            <table className="trd-table">
              <thead>
                <tr>
                  {canWrite && !isImmutable && (
                    <th className="trd-th-check">
                      <input
                        type="checkbox"
                        className="trd-checkbox"
                        checked={paginated.length > 0 && selectedExecutionIds.size === paginated.length}
                        onChange={toggleSelectAll}
                        title="Select all on this page"
                      />
                    </th>
                  )}
                  {isColVisible('title') && <th className="trd-col-title">{titleColumnLabel.toUpperCase()}</th>}
                  {isColVisible('status') && <th className="trd-col-status">STATUS</th>}
                  {isColVisible('executed_by') && <th className="trd-col-executed-by">EXECUTED BY</th>}
                  {isColVisible('executed_at') && <th className="trd-col-executed-at">EXECUTED AT</th>}
                  {isColVisible('defect') && <th className="trd-col-defect">DEFECT / NOTE</th>}
                  {isColVisible('evidence') && <th className="trd-col-evidence">EVIDENCE</th>}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td
                      colSpan={(canWrite && !isImmutable ? 1 : 0) + columns.filter(c => c.visible).length}
                      className="trd-empty"
                    >No test cases found</td>
                  </tr>
                ) : paginated.map(execution => (
                  <tr key={execution.id} className={selectedExecutionIds.has(execution.id) ? 'trd-row-selected' : ''}>
                    {canWrite && !isImmutable && (
                      <td className="trd-td-check">
                        <input
                          type="checkbox"
                          className="trd-checkbox"
                          checked={selectedExecutionIds.has(execution.id)}
                          onChange={() => toggleSelectExecution(execution.id)}
                        />
                      </td>
                    )}
                    {isColVisible('title') && (
                      <td className="trd-col-title">
                        <div
                          className="trd-tc-name"
                          dangerouslySetInnerHTML={{ __html: execution.test_case_description || '' }}
                        />
                        {execution.test_case_display_id && (
                          <div className="trd-tc-id">{execution.test_case_display_id}</div>
                        )}
                      </td>
                    )}
                    {isColVisible('status') && (
                      <td className="trd-col-status">
                        <span className={`trd-exec-badge ${getExecStatusClass(execution.execution_status)}`}>
                          {execution.execution_status.replace('_', ' ')}
                        </span>
                      </td>
                    )}
                    {isColVisible('executed_by') && (
                      <td className="trd-col-executed-by trd-td-muted">{execution.executed_by ? emailToUsername(execution.executed_by) : '—'}</td>
                    )}
                    {isColVisible('executed_at') && (
                      <td className="trd-col-executed-at trd-td-muted">{formatTime(execution.executed_at)}</td>
                    )}
                    {isColVisible('defect') && (
                    <td className="trd-col-defect">
                      {execution.defect_reference
                        ? <span className="trd-defect-ref">
                            {parseJiraTickets(execution.defect_reference).map((seg, i) =>
                              typeof seg === 'string'
                                ? <React.Fragment key={i}>{seg}</React.Fragment>
                                : <a
                                    key={i}
                                    href={seg.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="trd-defect-jira-link"
                                    title={`Open ${seg.ticket} in Jira`}
                                    onClick={e => e.stopPropagation()}
                                  >{seg.ticket}</a>
                            )}
                          </span>
                        : <span className="trd-td-muted">—</span>
                      }
                    </td>
                    )}
                    {isColVisible('evidence') && (
                    <td className="trd-col-evidence">
                      <div className="trd-action-cell">
                        {!isImmutable && canWrite ? (
                          <button
                            className="trd-action-btn"
                            onClick={() => handleExecuteTestCase(execution)}
                            title={execution.execution_status === 'NOT_RUN' ? 'Execute' : 'Re-execute'}
                          >
                            {execution.execution_status === 'NOT_RUN' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="5 3 19 12 5 21 5 3"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
                              </svg>
                            )}
                          </button>
                        ) : !isImmutable ? (
                          /* Viewer on an active run — no Execute action; Preview (below) still
                             covers viewing any evidence already uploaded for this row. */
                          <span className="trd-td-muted">—</span>
                        ) : (
                          /* Immutable run — show evidence download button or reference note.
                             Download is a read-only action, so it's shown for Viewer too. */
                          (() => {
                            // If this execution is a bulk-upload reference (no actual file),
                            // hide the download button — the DEFECT/NOTE column already shows the reference
                            const isEvidenceRef = execution.defect_reference?.startsWith('Evidence same as');
                            if (isEvidenceRef) {
                              return <span className="trd-td-muted">—</span>;
                            }

                            const isLoadingEvidence = evidenceLoading[execution.id];
                            const files = evidenceMap[execution.id]; // undefined = not yet fetched
                            const hasNoEvidence = files !== undefined && files.length === 0;

                            return (
                              <button
                                className={`trd-action-btn trd-download-btn${hasNoEvidence ? ' trd-download-btn--disabled' : ''}`}
                                onClick={() => !hasNoEvidence && handleEvidenceDownloadClick(execution.id)}
                                disabled={isLoadingEvidence || hasNoEvidence}
                                title={
                                  isLoadingEvidence ? 'Loading evidence...'
                                  : hasNoEvidence ? 'No evidence uploaded'
                                  : 'Download evidence'
                                }
                              >
                                {isLoadingEvidence ? (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="trd-spin">
                                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                                  </svg>
                                ) : (
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                )}
                              </button>
                            );
                          })()
                        )}
                        {/* Preview — shown whenever evidence exists, regardless of run status */}
                        {(evidenceMap[execution.id]?.length ?? 0) > 0 && (
                          <button
                            className="trd-action-btn trd-preview-btn"
                            onClick={() => openPreview(execution.id)}
                            title="Preview evidence"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="trd-pagination">
              <span className="trd-pagination-info">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} test cases
              </span>
              <div className="trd-pagination-btns">
                <button
                  className="trd-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="trd-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showExecutionModal && selectedExecution && (
        <TestExecutionModal
          execution={selectedExecution}
          onClose={() => setShowExecutionModal(false)}
          onComplete={handleExecutionComplete}
        />
      )}

      {/* Evidence Preview — full-screen, left: test case context, right: evidence */}
      {showEvidenceModal && previewExecution && (
        <div className="trd-preview-overlay">
          <div className="trd-preview-fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="trd-preview-topbar">
              <div className="trd-preview-topbar-info">
                <span className="trd-preview-topbar-id">{previewExecution.test_case_display_id}</span>
                <span className={`trd-exec-badge ${getExecStatusClass(previewExecution.execution_status)}`}>
                  {previewExecution.execution_status.replace('_', ' ')}
                </span>
                <span className="trd-preview-topbar-position">{previewIndex + 1} of {filtered.length}</span>
              </div>
              <div className="trd-preview-topbar-actions">
                <button
                  className="trd-preview-nav-btn"
                  disabled={previewIndex <= 0}
                  onClick={() => goToPreviewIndex(previewIndex - 1)}
                  title="Previous"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  className="trd-preview-nav-btn"
                  disabled={previewIndex >= filtered.length - 1}
                  onClick={() => goToPreviewIndex(previewIndex + 1)}
                  title="Next"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
                <button className="trd-preview-close-btn" onClick={() => setShowEvidenceModal(false)} title="Close">×</button>
              </div>
            </div>
            <div className="trd-preview-split">
              <div className="trd-preview-left">
                <div className="trd-preview-field">
                  <h4>{previewExecution.test_case_display_id?.startsWith('TS') ? 'Scenario Title' : 'Test Case Title'}</h4>
                  <div className="trd-preview-rich trd-preview-title-text" dangerouslySetInnerHTML={{ __html: previewExecution.test_case_description || '—' }} />
                </div>

                {previewExecution.test_case_priority && (
                  <div className="trd-preview-field">
                    <h4>Priority</h4>
                    {priorityBadge(previewExecution.test_case_priority)}
                  </div>
                )}

                {(previewExecution.tags?.length ?? 0) > 0 && (
                  <div className="trd-preview-field">
                    <h4>Tags</h4>
                    <div className="trd-preview-tags">
                      {previewExecution.tags!.map(tag => (
                        <span key={tag} className="trd-preview-tag-chip">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {previewExecution.test_case_preconditions && (
                  <div className="trd-preview-field">
                    <h4>Preconditions</h4>
                    <div className="trd-preview-rich" dangerouslySetInnerHTML={{ __html: previewExecution.test_case_preconditions }} />
                  </div>
                )}

                <div className="trd-preview-field">
                  <h4>Expected Result</h4>
                  <div className="trd-preview-rich" dangerouslySetInnerHTML={{ __html: previewExecution.test_case_expected_result || '—' }} />
                </div>

                {previewExecution.comment && (
                  <div className="trd-preview-field">
                    <h4>Comments</h4>
                    <div className="trd-preview-rich">{previewExecution.comment}</div>
                  </div>
                )}
              </div>
              <div className="trd-preview-right">
                {!evidenceMap[previewExecution.id] ? (
                  <div className="trd-evidence-preview-placeholder">Loading evidence…</div>
                ) : evidenceMap[previewExecution.id].length === 0 ? (
                  <p className="trd-evidence-empty">No evidence files uploaded</p>
                ) : (
                  <div className="trd-evidence-preview-list">
                    {evidenceMap[previewExecution.id].map((file: any) => {
                      const kind = getEvidenceKind(file.file_name || '');
                      const url = previewUrls[file.id];
                      const isLoading = previewLoading[file.id];
                      const isZoomed = zoomedFileId === file.id;
                      return (
                        <div key={file.id} className="trd-evidence-preview-item">
                          <div className={`trd-evidence-preview-media${isZoomed ? ' trd-evidence-preview-media--zoomed' : ''}`}>
                            {kind === 'image' && url ? (
                              <img
                                src={url}
                                alt={file.file_name}
                                className={`trd-evidence-preview-img${isZoomed ? ' trd-evidence-preview-img--zoomed' : ''}`}
                                onClick={() => setZoomedFileId(isZoomed ? null : file.id)}
                                title={isZoomed ? 'Click to zoom out' : 'Click to zoom in'}
                              />
                            ) : kind === 'video' && url ? (
                              <video src={url} controls className="trd-evidence-preview-video" />
                            ) : isLoading ? (
                              <div className="trd-evidence-preview-placeholder">Loading preview…</div>
                            ) : (
                              <div className="trd-evidence-preview-placeholder">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                                <span>Preview not available</span>
                              </div>
                            )}
                          </div>
                          <div className="trd-evidence-preview-footer">
                            <div className="trd-evidence-file-info">
                              <div className="trd-evidence-file-name">{file.file_name || file.id}</div>
                              <div className="trd-evidence-file-date">{new Date(file.uploaded_at).toLocaleDateString()}</div>
                            </div>
                            <button
                              className="trd-evidence-download-btn"
                              onClick={() => triggerEvidenceDownload(file)}
                              title="Download"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Evidence Upload Modal */}
      {showBulkUpload && (
        <div className="trd-bulk-overlay" onClick={() => { if (!bulkUploading) setShowBulkUpload(false); }}>
          <div className="trd-bulk-modal" onClick={e => e.stopPropagation()}>
            <div className="trd-bulk-modal-header">
              <h3>Upload Evidence to {selectedExecutionIds.size} Execution{selectedExecutionIds.size !== 1 ? 's' : ''}</h3>
              <button className="trd-bulk-modal-close" onClick={() => setShowBulkUpload(false)} disabled={bulkUploading}>×</button>
            </div>
            <div className="trd-bulk-modal-body">
              <p className="trd-bulk-desc">
                The file will be uploaded once (to the first selected execution).
                All {selectedExecutionIds.size} execution{selectedExecutionIds.size !== 1 ? 's' : ''} will be marked as <strong style={{ color: '#16a34a' }}>PASS</strong>.
                The remaining {selectedExecutionIds.size - 1} will have a note added in the <strong>Defect / Note</strong> column referencing the first execution's evidence.
                {testRun?.environment && <span className="trd-bulk-env"> Environment: <strong>{testRun.environment}</strong></span>}
              </p>

              <div className="trd-bulk-file-area" onClick={() => bulkFileInputRef.current?.click()}>
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setBulkFile(f); setBulkError(null); }
                  }}
                />
                {bulkFile ? (
                  <div className="trd-bulk-file-selected">
                    <span className="trd-bulk-file-icon">{bulkFile.type.startsWith('video/') ? '🎥' : '🖼️'}</span>
                    <div>
                      <div className="trd-bulk-file-name">{bulkFile.name}</div>
                      <div className="trd-bulk-file-size">{(bulkFile.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button className="trd-bulk-file-change" onClick={e => { e.stopPropagation(); bulkFileInputRef.current?.click(); }}>Change</button>
                  </div>
                ) : (
                  <div className="trd-bulk-file-placeholder">
                    <span style={{ fontSize: '2rem' }}>📎</span>
                    <span>Click to select an image or video (max 200MB)</span>
                  </div>
                )}
              </div>

              {bulkProgress && (
                <div className="trd-bulk-progress">
                  <div className="trd-bulk-progress-bar">
                    <div
                      className="trd-bulk-progress-fill"
                      style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
                    />
                  </div>
                  <span className="trd-bulk-progress-text">
                    {bulkUploading
                      ? `Uploading ${bulkProgress.done} / ${bulkProgress.total}...`
                      : `Done — ${bulkProgress.done} / ${bulkProgress.total} uploaded`}
                  </span>
                </div>
              )}

              {bulkError && <div className="trd-bulk-error">{bulkError}</div>}
            </div>
            <div className="trd-bulk-modal-footer">
              <button className="trd-bulk-cancel" onClick={() => setShowBulkUpload(false)} disabled={bulkUploading}>Cancel</button>
              <button
                className="trd-bulk-upload-btn"
                onClick={handleBulkUpload}
                disabled={!bulkFile || bulkUploading}
              >
                {bulkUploading ? `Uploading ${bulkProgress?.done}/${bulkProgress?.total}...` : `📤 Upload to ${selectedExecutionIds.size} Execution${selectedExecutionIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Change Modal */}
      {showBulkStatus && (
        <div className="trd-bulk-overlay" onClick={() => { if (!bulkStatusUpdating) setShowBulkStatus(false); }}>
          <div className="trd-bulk-modal" onClick={e => e.stopPropagation()}>
            <div className="trd-bulk-modal-header">
              <h3>Change Status for {selectedExecutionIds.size} Execution{selectedExecutionIds.size !== 1 ? 's' : ''}</h3>
              <button className="trd-bulk-modal-close" onClick={() => setShowBulkStatus(false)} disabled={bulkStatusUpdating}>×</button>
            </div>
            <div className="trd-bulk-modal-body">
              <p className="trd-bulk-desc">
                All {selectedExecutionIds.size} selected execution{selectedExecutionIds.size !== 1 ? 's' : ''} will be updated to the status below. Comment and Defect/Note are optional and, if filled in, will be applied to every selected execution.
              </p>

              <div className="trd-bulk-status-group">
                <label className="trd-bulk-field-label">Execution Status *</label>
                <div className="trd-bulk-status-options">
                  {(['PASS', 'FAIL', 'BLOCKED'] as const).map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`trd-bulk-status-opt ${getExecStatusClass(s)}${bulkStatus === s ? ' selected' : ''}`}
                      onClick={() => setBulkStatus(s)}
                      disabled={bulkStatusUpdating}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="trd-bulk-status-group">
                <label className="trd-bulk-field-label" htmlFor="bulk-status-comment">Comments (optional)</label>
                <textarea
                  id="bulk-status-comment"
                  className="trd-bulk-textarea"
                  rows={3}
                  placeholder="Add a note that applies to all selected executions..."
                  value={bulkStatusComment}
                  onChange={e => setBulkStatusComment(e.target.value)}
                  disabled={bulkStatusUpdating}
                />
              </div>

              <div className="trd-bulk-status-group">
                <label className="trd-bulk-field-label" htmlFor="bulk-status-defect">Defect / Note (optional)</label>
                <input
                  id="bulk-status-defect"
                  type="text"
                  className="trd-bulk-input"
                  placeholder="e.g., PLANNER-545"
                  value={bulkStatusDefect}
                  onChange={e => setBulkStatusDefect(e.target.value)}
                  disabled={bulkStatusUpdating}
                />
              </div>

              {bulkStatusProgress && (
                <div className="trd-bulk-progress">
                  <div className="trd-bulk-progress-bar">
                    <div
                      className="trd-bulk-progress-fill"
                      style={{ width: `${Math.round((bulkStatusProgress.done / bulkStatusProgress.total) * 100)}%` }}
                    />
                  </div>
                  <span className="trd-bulk-progress-text">
                    {bulkStatusUpdating
                      ? `Updating ${bulkStatusProgress.done} / ${bulkStatusProgress.total}...`
                      : `Done — ${bulkStatusProgress.done} / ${bulkStatusProgress.total} updated`}
                  </span>
                </div>
              )}

              {bulkStatusError && <div className="trd-bulk-error">{bulkStatusError}</div>}
            </div>
            <div className="trd-bulk-modal-footer">
              <button className="trd-bulk-cancel" onClick={() => setShowBulkStatus(false)} disabled={bulkStatusUpdating}>Cancel</button>
              <button
                className="trd-bulk-upload-btn"
                onClick={handleBulkStatusChange}
                disabled={bulkStatusUpdating}
              >
                {bulkStatusUpdating
                  ? `Updating ${bulkStatusProgress?.done}/${bulkStatusProgress?.total}...`
                  : `Set ${selectedExecutionIds.size} Execution${selectedExecutionIds.size !== 1 ? 's' : ''} to ${bulkStatus}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunDetailPage;
